import L from "leaflet";
import { haversineKm } from "./distance";
import { reverseLookup } from "./geocode";
import { icon } from "./icons";
import { i18n, type MessageKey } from "./i18n";
import { stops, type StopChangeDetail } from "./stops";
import { theme } from "./theme";
import { HOTEL_ROUTE_ID } from "./day-route";
import type { DayRoute, Stop } from "./types";

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

const DEFAULT_CENTER: L.LatLngExpression = [20, 0];
const DEFAULT_ZOOM = 2;
const FLY_ZOOM = 11;
const REGEOCODE_THRESHOLD_KM = 5;
const MAP_STYLE_KEY = "tripPlanner.mapStyle";
const PLACES_VISIBLE_KEY = "tripPlanner.placesVisible";
const PLACES_FILTER_KEY = "tripPlanner.placesFilter";
const HOTELS_VISIBLE_KEY = "tripPlanner.hotelsVisible";
const EXCURSIONS_VISIBLE_KEY = "tripPlanner.excursionsVisible";

type MapStyleId = "osm" | "voyager" | "topo";

let map: L.Map | null = null;
const markers = new Map<string, L.Marker>();
const dragLookups = new Map<string, AbortController>();
let routeLine: L.Polyline | null = null;
let suggestionPopup: L.Popup | null = null;
let pendingReverse: AbortController | null = null;
let layersControl: L.Control.Layers | null = null;
const baseLayers = new Map<MapStyleId, L.TileLayer>();
let placesLayer: L.LayerGroup | null = null;
let hotelLayer: L.LayerGroup | null = null;
let excursionLayer: L.LayerGroup | null = null;
let dayRouteLayer: L.LayerGroup | null = null;
let placesFilterIds: Set<string> = new Set(["all"]);
let placesFilterControl: { update: (all: readonly Stop[]) => void } | null = null;
let placePickCallback: ((lat: number, lon: number) => void) | null = null;

function routeStyle(): L.PolylineOptions {
  return {
    color: cssVar("--marker-stop", "#b2622d"),
    weight: 2.5,
    opacity: 0.85,
    dashArray: "2 7",
    lineCap: "round",
  };
}

const STYLE_LABELS: Record<MapStyleId, MessageKey> = {
  osm: "mapStyleOsm",
  voyager: "mapStyleVoyager",
  topo: "mapStyleTopo",
};

export function initMap(elementId: string): void {
  map = L.map(elementId, {
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    worldCopyJump: true,
    zoomControl: false,
  });
  L.control.zoom({ position: "bottomright" }).addTo(map);

  setupBaseLayers(map);
  setupPlacesLayer(map);
  setupHotelLayer(map);
  setupExcursionLayer(map);
  dayRouteLayer = L.layerGroup().addTo(map);
  refreshLayersControl();
  addFitControl(map);
  addPlacesFilterControl(map);
  map.on("click", onMapClick);

  stops.addEventListener("change", (event) => {
    const detail = (event as CustomEvent<StopChangeDetail>).detail;
    handleStateChange(detail);
  });

  i18n.addEventListener("change", () => {
    refreshLayersControl();
    placesFilterControl?.update(stops.all());
  });

  theme.addEventListener("change", () => {
    routeLine?.setStyle(routeStyle());
    rebuildPlacesMarkers();
  });
}

function setupPlacesLayer(target: L.Map): void {
  placesLayer = L.layerGroup();
  try {
    const raw = localStorage.getItem(PLACES_FILTER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) placesFilterIds = new Set(parsed as string[]);
    }
  } catch { /* ignore */ }
  const savedVisible = localStorage.getItem(PLACES_VISIBLE_KEY);
  if (savedVisible !== "false") {
    placesLayer.addTo(target);
  }
  target.on("overlayadd", (e: L.LayersControlEvent) => {
    if (e.layer === placesLayer) {
      try { localStorage.setItem(PLACES_VISIBLE_KEY, "true"); } catch { /* ignore */ }
    }
  });
  target.on("overlayremove", (e: L.LayersControlEvent) => {
    if (e.layer === placesLayer) {
      try { localStorage.setItem(PLACES_VISIBLE_KEY, "false"); } catch { /* ignore */ }
    }
  });
}

function setupHotelLayer(target: L.Map): void {
  hotelLayer = L.layerGroup();
  const savedVisible = localStorage.getItem(HOTELS_VISIBLE_KEY);
  if (savedVisible !== "false") {
    hotelLayer.addTo(target);
  }
  target.on("overlayadd", (e: L.LayersControlEvent) => {
    if (e.layer === hotelLayer) {
      try { localStorage.setItem(HOTELS_VISIBLE_KEY, "true"); } catch { /* ignore */ }
    }
  });
  target.on("overlayremove", (e: L.LayersControlEvent) => {
    if (e.layer === hotelLayer) {
      try { localStorage.setItem(HOTELS_VISIBLE_KEY, "false"); } catch { /* ignore */ }
    }
  });
}

function setupExcursionLayer(target: L.Map): void {
  excursionLayer = L.layerGroup();
  const savedVisible = localStorage.getItem(EXCURSIONS_VISIBLE_KEY);
  if (savedVisible !== "false") {
    excursionLayer.addTo(target);
  }
  target.on("overlayadd", (e: L.LayersControlEvent) => {
    if (e.layer === excursionLayer) {
      try { localStorage.setItem(EXCURSIONS_VISIBLE_KEY, "true"); } catch { /* ignore */ }
    }
  });
  target.on("overlayremove", (e: L.LayersControlEvent) => {
    if (e.layer === excursionLayer) {
      try { localStorage.setItem(EXCURSIONS_VISIBLE_KEY, "false"); } catch { /* ignore */ }
    }
  });
}

export function setPlacePickMode(cb: ((lat: number, lon: number) => void) | null): void {
  placePickCallback = cb;
  if (map) {
    map.getContainer().style.cursor = cb ? "crosshair" : "";
  }
}

function setupBaseLayers(target: L.Map): void {
  baseLayers.set(
    "osm",
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }),
  );
  baseLayers.set(
    "voyager",
    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      {
        maxZoom: 20,
        subdomains: "abcd",
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      },
    ),
  );
  baseLayers.set(
    "topo",
    L.tileLayer("https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", {
      maxZoom: 17,
      attribution:
        'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> contributors, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)',
    }),
  );

  const initial = readSavedStyle();
  baseLayers.get(initial)!.addTo(target);

  // No refreshLayersControl() here: the overlay layer groups don't exist yet. initMap calls it
  // once every layer is built, otherwise the control renders with base layers only until
  // something else happens to rebuild it (which used to be a language toggle, and nothing else).
  target.on("baselayerchange", (e: L.LayersControlEvent) => {
    const id = layerIdFromName(e.name);
    if (id) {
      try {
        localStorage.setItem(MAP_STYLE_KEY, id);
      } catch {
        // ignore
      }
    }
  });
}

function readSavedStyle(): MapStyleId {
  try {
    const saved = localStorage.getItem(MAP_STYLE_KEY);
    if (saved === "osm" || saved === "voyager" || saved === "topo") return saved;
  } catch {
    // ignore
  }
  return "osm";
}

function refreshLayersControl(): void {
  if (!map) return;
  if (layersControl) {
    layersControl.remove();
    layersControl = null;
  }
  const labelled: Record<string, L.TileLayer> = {};
  for (const [id, layer] of baseLayers.entries()) {
    labelled[i18n.t(STYLE_LABELS[id])] = layer;
  }
  const overlays: Record<string, L.LayerGroup> = {};
  if (hotelLayer) overlays[i18n.t("layerHotels")] = hotelLayer;
  if (placesLayer) overlays[i18n.t("tabPlaces")] = placesLayer;
  if (excursionLayer) overlays[i18n.t("layerExcursions")] = excursionLayer;
  layersControl = L.control
    .layers(labelled, overlays, { position: "topright", collapsed: true })
    .addTo(map);
}

function layerIdFromName(name: string): MapStyleId | null {
  for (const [id, key] of Object.entries(STYLE_LABELS) as [MapStyleId, MessageKey][]) {
    if (i18n.t(key) === name) return id;
  }
  return null;
}

export function focusStop(stop: Stop): void {
  if (!map) return;
  map.flyTo([stop.lat, stop.lon], FLY_ZOOM, { duration: 0.8 });
}

/** Pan to an arbitrary point — used when an excursion row with coords is clicked. */
export function focusPoint(lat: number, lon: number): void {
  if (!map) return;
  map.flyTo([lat, lon], FLY_ZOOM, { duration: 0.8 });
}

function handleStateChange(detail: StopChangeDetail): void {
  if (!map) return;
  rebuildMarkers();
  rebuildRoute();
  rebuildPlacesMarkers();
  rebuildHotelMarkers();
  rebuildExcursionMarkers();
  placesFilterControl?.update(stops.all());
  if (detail.kind === "load") fitToStops();
}

function rebuildMarkers(): void {
  for (const marker of markers.values()) marker.remove();
  markers.clear();
  const all = stops.all();
  all.forEach((stop, index) => {
    const marker = L.marker([stop.lat, stop.lon], {
      draggable: true,
      autoPan: true,
    }).addTo(map!);
    marker.bindTooltip(labelHtml(stop, index + 1), {
      permanent: true,
      direction: "top",
      offset: [-15, -8],
      className: "stop-label",
    });
    marker.on("dragend", () => onMarkerDragEnd(stop.id, marker));
    markers.set(stop.id, marker);
  });
}

function rebuildPlacesMarkers(): void {
  if (!placesLayer) return;
  placesLayer.clearLayers();
  if (placesFilterIds.has("none") || placesFilterIds.size === 0) return;
  const showAll = placesFilterIds.has("all");
  for (const stop of stops.all()) {
    if (!showAll && !placesFilterIds.has(stop.id)) continue;
    for (const place of stop.places ?? []) {
      if (place.lat == null || place.lon == null) continue;
      const placeColor = cssVar("--marker-place", "#a87c1f");
      L.circleMarker([place.lat, place.lon], {
        radius: 6,
        color: placeColor,
        fillColor: placeColor,
        fillOpacity: 0.85,
        weight: 2,
      })
        .bindTooltip(escapeHtml(place.name), { direction: "top", offset: [0, -6] })
        .addTo(placesLayer);
    }
  }
}

function rebuildHotelMarkers(): void {
  if (!hotelLayer) return;
  hotelLayer.clearLayers();
  for (const stop of stops.all()) {
    const h = stop.hotel;
    if (h?.lat == null || h?.lon == null) continue;
    const markerIcon = L.divIcon({
      html: `<div class="hotel-marker-icon">${icon("hotel", { size: 16 }).outerHTML}</div>`,
      className: "",
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      tooltipAnchor: [0, -36],
    });
    L.marker([h.lat, h.lon], { icon: markerIcon })
      .bindTooltip(escapeHtml(h.name ?? stop.name), { direction: "top", offset: [0, -4] })
      .addTo(hotelLayer);
  }
}

function rebuildExcursionMarkers(): void {
  if (!excursionLayer) return;
  excursionLayer.clearLayers();
  for (const stop of stops.all()) {
    for (const exc of stop.excursions ?? []) {
      // An excursion may carry only a maps link and no coords — nothing to draw for those.
      if (exc.lat == null || exc.lon == null) continue;
      const markerIcon = L.divIcon({
        html: `<div class="excursion-marker-icon">${icon("excursion", { size: 16 }).outerHTML}</div>`,
        className: "",
        iconSize: [28, 36],
        iconAnchor: [14, 36],
        tooltipAnchor: [0, -36],
      });
      L.marker([exc.lat, exc.lon], { icon: markerIcon })
        .bindTooltip(escapeHtml(exc.name), { direction: "top", offset: [0, -4] })
        .addTo(excursionLayer);
    }
  }
}

function onMarkerDragEnd(stopId: string, marker: L.Marker): void {
  const current = stops.get(stopId);
  if (!current) return;
  const { lat, lng: lon } = marker.getLatLng();
  const moved = haversineKm(current, { lat, lon });
  stops.update(stopId, { lat, lon });

  if (moved <= REGEOCODE_THRESHOLD_KM) return;

  dragLookups.get(stopId)?.abort();
  const controller = new AbortController();
  dragLookups.set(stopId, controller);

  reverseLookup(lat, lon, controller.signal)
    .then((hit) => {
      if (controller.signal.aborted || !hit) return;
      stops.update(stopId, { city: hit.city, name: hit.city });
    })
    .catch((err) => {
      if ((err as Error).name === "AbortError") return;
    })
    .finally(() => {
      if (dragLookups.get(stopId) === controller) {
        dragLookups.delete(stopId);
      }
    });
}

function rebuildRoute(): void {
  if (!map) return;
  const coords = stops.all().map((s) => [s.lat, s.lon] as L.LatLngTuple);
  if (coords.length < 2) {
    routeLine?.remove();
    routeLine = null;
    return;
  }
  if (routeLine) {
    routeLine.setLatLngs(coords);
  } else {
    routeLine = L.polyline(coords, routeStyle()).addTo(map);
  }
}

function labelHtml(stop: Stop, order: number): string {
  return `<span class="stop-label-num">${order}</span> ${escapeHtml(stop.name)}`;
}

export function fitToStops(): void {
  if (!map) return;
  const all = stops.all();
  if (all.length === 0) return;
  if (all.length === 1) {
    map.setView([all[0].lat, all[0].lon], FLY_ZOOM);
    return;
  }
  const bounds = L.latLngBounds(all.map((s) => [s.lat, s.lon] as [number, number]));
  map.fitBounds(bounds, { padding: [40, 40] });
}

function addPlacesFilterControl(target: L.Map): void {
  const FilterControl = L.Control.extend({
    _container: null as HTMLElement | null,
    _select: null as HTMLSelectElement | null,

    onAdd(): HTMLElement {
      const container = L.DomUtil.create(
        "div",
        "places-filter-control leaflet-bar leaflet-control",
      );
      const select = L.DomUtil.create(
        "select",
        "places-filter-select",
        container,
      ) as HTMLSelectElement;
      select.multiple = true;
      select.setAttribute("aria-label", i18n.t("placesFilterAll"));
      L.DomEvent.on(select, "change", () => {
        const selected = Array.from(select.selectedOptions).map((o) => o.value);
        let next: string[];
        if (selected.includes("none") && !placesFilterIds.has("none")) {
          next = ["none"];
        } else if (selected.includes("all") && !placesFilterIds.has("all")) {
          next = ["all"];
        } else {
          next = selected.filter((v) => v !== "all" && v !== "none");
          if (next.length === 0) next = ["all"];
        }
        placesFilterIds = new Set(next);
        for (const opt of Array.from(select.options)) {
          opt.selected = placesFilterIds.has(opt.value);
        }
        try { localStorage.setItem(PLACES_FILTER_KEY, JSON.stringify([...placesFilterIds])); } catch { /* ignore */ }
        rebuildPlacesMarkers();
      });
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      this._container = container;
      this._select = select;
      this.update(stops.all());
      return container;
    },

    update(allStops: readonly Stop[]): void {
      const select = this._select as HTMLSelectElement | null;
      const container = this._container as HTMLElement | null;
      if (!select || !container) return;

      const stopsWithPlaces = allStops.filter(
        (s) => (s.places ?? []).some((p) => p.lat != null && p.lon != null),
      );

      if (stopsWithPlaces.length === 0) {
        container.style.display = "none";
        return;
      }
      container.style.display = "";

      select.innerHTML = "";

      const allOpt = document.createElement("option");
      allOpt.value = "all";
      allOpt.textContent = i18n.t("placesFilterAll");
      select.appendChild(allOpt);

      const noneOpt = document.createElement("option");
      noneOpt.value = "none";
      noneOpt.textContent = i18n.t("placesFilterNone");
      select.appendChild(noneOpt);

      for (const s of stopsWithPlaces) {
        const opt = document.createElement("option");
        opt.value = s.id;
        opt.textContent = s.name;
        select.appendChild(opt);
      }

      // Validate persisted IDs — drop any stop IDs no longer present
      const validIds = new Set(["all", "none", ...stopsWithPlaces.map((s) => s.id)]);
      const stillValid = [...placesFilterIds].filter((id) => validIds.has(id));
      if (stillValid.length !== placesFilterIds.size) {
        placesFilterIds = new Set(stillValid.length > 0 ? stillValid : ["all"]);
        try { localStorage.setItem(PLACES_FILTER_KEY, JSON.stringify([...placesFilterIds])); } catch { /* ignore */ }
        rebuildPlacesMarkers();
      }

      for (const opt of Array.from(select.options)) {
        opt.selected = placesFilterIds.has(opt.value);
      }

      select.size = Math.min(stopsWithPlaces.length + 2, 6);
    },
  });

  const ctrl = new FilterControl({ position: "topright" });
  ctrl.addTo(target);
  placesFilterControl = ctrl as unknown as { update: (all: readonly Stop[]) => void };
}

function addFitControl(target: L.Map): void {
  const FitControl = L.Control.extend({
    onAdd(): HTMLElement {
      const container = L.DomUtil.create(
        "div",
        "leaflet-bar leaflet-control fit-control",
      );
      const button = L.DomUtil.create("a", "fit-button", container);
      button.href = "#";
      button.dataset.i18nTitle = "fitToAllStops";
      button.title = i18n.t("fitToAllStops");
      button.setAttribute("role", "button");
      button.setAttribute("aria-label", i18n.t("fitToAllStops"));
      button.appendChild(icon("expand", { size: 16 }));
      L.DomEvent.on(button, "click", (e: Event) => {
        L.DomEvent.preventDefault(e);
        L.DomEvent.stopPropagation(e);
        fitToStops();
      });
      L.DomEvent.disableClickPropagation(container);
      return container;
    },
  });
  new FitControl({ position: "topleft" }).addTo(target);
}

async function onMapClick(e: L.LeafletMouseEvent): Promise<void> {
  if (!map) return;

  if (placePickCallback) {
    const cb = placePickCallback;
    placePickCallback = null;
    map.getContainer().style.cursor = "";
    cb(e.latlng.lat, e.latlng.lng);
    return;
  }

  if (suggestionPopup) {
    suggestionPopup.remove();
    suggestionPopup = null;
  }
  pendingReverse?.abort();
  pendingReverse = new AbortController();

  const loading = L.popup()
    .setLatLng(e.latlng)
    .setContent(`<em>${i18n.t("lookingUp")}</em>`)
    .openOn(map);
  suggestionPopup = loading;

  try {
    const hit = await reverseLookup(e.latlng.lat, e.latlng.lng, pendingReverse.signal);
    if (!map) return;
    if (suggestionPopup !== loading) return;

    if (!hit) {
      suggestionPopup
        .setLatLng(e.latlng)
        .setContent(`<em>${i18n.t("noCityHere")}</em>`);
      return;
    }

    const popup = L.popup({ autoClose: true, closeOnClick: true }).setLatLng([
      hit.lat,
      hit.lon,
    ]);
    const html = document.createElement("div");
    html.className = "suggest-popup";
    html.innerHTML = `
      <p>${i18n.t("addCityPrompt", { city: escapeHtml(hit.city) })}</p>
      <button type="button" class="primary" data-action="add">${escapeHtml(i18n.t("addToTrip"))}</button>
      <button type="button" class="ghost" data-action="cancel">${escapeHtml(i18n.t("cancel"))}</button>
    `;
    html.querySelector('[data-action="add"]')?.addEventListener("click", () => {
      stops.add({ name: hit.city, city: hit.city, lat: hit.lat, lon: hit.lon });
      popup.remove();
      suggestionPopup = null;
    });
    html.querySelector('[data-action="cancel"]')?.addEventListener("click", () => {
      popup.remove();
      suggestionPopup = null;
    });
    popup.setContent(html).openOn(map);
    suggestionPopup = popup;
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    if (!map || suggestionPopup !== loading) return;
    suggestionPopup.setContent(`<em>${i18n.t("lookupFailed")}</em>`);
  }
}

export function showDayRoute(stop: Stop, route: DayRoute): void {
  if (!map || !dayRouteLayer) return;
  dayRouteLayer.clearLayers();

  const placeMap = new Map((stop.places ?? []).map((p) => [p.id, p]));
  const hotel = stop.hotel;

  function resolveCoord(id: string): { lat: number; lon: number } | undefined {
    if (id === HOTEL_ROUTE_ID && hotel?.lat != null && hotel.lon != null)
      return { lat: hotel.lat, lon: hotel.lon };
    const p = placeMap.get(id);
    return p?.lat != null && p.lon != null ? { lat: p.lat, lon: p.lon } : undefined;
  }

  // Draw leg polylines
  for (const leg of route.legs) {
    const from = resolveCoord(leg.fromId);
    const to = resolveCoord(leg.toId);
    const coords: L.LatLngExpression[] =
      leg.polyline ??
      (from != null && to != null
        ? [
            [from.lat, from.lon],
            [to.lat, to.lon],
          ]
        : []);
    if (!coords.length) continue;

    const style: L.PolylineOptions =
      leg.mode === "walk"
        ? { color: cssVar("--marker-walk", "#42704c"), weight: 4, opacity: 0.85 }
        : { color: cssVar("--marker-dayroute", "#4e6e8d"), weight: 4, opacity: 0.85, dashArray: "7, 7" };

    L.polyline(coords, style).addTo(dayRouteLayer!);
  }

  // Draw numbered place markers
  route.orderedPlaceIds.forEach((id, idx) => {
    const place = placeMap.get(id);
    if (!place || place.lat == null || place.lon == null) return;
    const markerIcon = L.divIcon({
      html: `<div class="day-route-marker">${idx + 1}</div>`,
      className: "",
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
    L.marker([place.lat, place.lon], { icon: markerIcon })
      .bindTooltip(escapeHtml(place.name), { direction: "top", offset: [0, -14] })
      .addTo(dayRouteLayer!);
  });

  // Draw hotel marker for roundtrip
  if (route.roundtripHotel && hotel?.lat != null && hotel.lon != null) {
    const hotelIcon = L.divIcon({
      html: `<div class="day-route-marker day-route-marker--hotel">H</div>`,
      className: "",
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    });
    L.marker([hotel.lat, hotel.lon], { icon: hotelIcon })
      .bindTooltip(escapeHtml(route.roundtripHotel.name), { direction: "top", offset: [0, -14] })
      .addTo(dayRouteLayer!);
  }

  // Fit map to all route places (+ hotel if roundtrip)
  const allCoords: [number, number][] = [];
  for (const id of route.orderedPlaceIds) {
    const p = placeMap.get(id);
    if (p?.lat != null && p?.lon != null) allCoords.push([p.lat, p.lon]);
  }
  if (route.roundtripHotel && hotel?.lat != null && hotel.lon != null) {
    allCoords.push([hotel.lat, hotel.lon]);
  }
  if (allCoords.length > 1) {
    map.fitBounds(L.latLngBounds(allCoords), { padding: [40, 40] });
  } else if (allCoords.length === 1) {
    map.setView(allCoords[0], 14);
  }
}

export function hideDayRoute(): void {
  dayRouteLayer?.clearLayers();
}

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
